// === Admin: Users ===
// Full CRUD page — list, add, edit, delete.
// Ported from old Click2Gro (app/Controllers/Admin/UsersController.php)
// Uses Supabase Auth + profiles table for user data.

import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  FormControlLabel,
  Switch,
  InputLabel,
  FormControl,
  ListSubheader,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { AppTableHead, AppTableHeadCell, AppTableRow, DEFAULT_ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } from '../components/AppTable';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UserRow {
  readonly id: string;
  readonly email: string;
  readonly username: string;
  readonly first_name: string;
  readonly last_name: string;
  readonly phone_number: string;
  readonly mobile_number: string;
  readonly state: string;
  readonly suburb: string;
  readonly timezone: string;
  readonly role: string;
  readonly postcode: string;
  readonly address: string;
  readonly company_id: number | null;
  readonly active: boolean;
  readonly created_by: string;
  readonly created_at: string | null;
  readonly updated_at: string | null;
  readonly last_sign_in_at: string | null;
  readonly mfa_enabled: boolean;
}

interface SuburbOption {
  readonly id: number;
  readonly suburb: string;
  readonly postcode: string;
  readonly country: string;
}

interface FormState {
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  mobile_number: string;
  state: string;
  suburb: string;
  timezone: string;
  postcode: string;
  address: string;
  role: string;
  company_id: string;
  active: boolean;
}

const DEFAULT_FORM: FormState = {
  email: '',
  username: '',
  first_name: '',
  last_name: '',
  phone_number: '',
  mobile_number: '',
  state: '',
  suburb: '',
  timezone: 'Australia/Adelaide',
  postcode: '',
  address: '',
  role: 'superadmin',
  company_id: '',
  active: true,
};

const AU_STATES = [
  { value: 'ACT', label: 'ACT' },
  { value: 'NSW', label: 'NSW' },
  { value: 'NT', label: 'NT' },
  { value: 'QLD', label: 'QLD' },
  { value: 'SA', label: 'SA' },
  { value: 'TAS', label: 'TAS' },
  { value: 'VIC', label: 'VIC' },
  { value: 'WA', label: 'WA' },
] as const;

// Grouped by country. Users of a UK company are set to Europe/London by the
// database (trg_profiles_company_timezone), so their row shows it here.
const TIMEZONE_GROUPS = [
  {
    country: 'Australia',
    zones: [
      { value: 'Australia/Adelaide', label: 'Australia/Adelaide' },
      { value: 'Australia/Brisbane', label: 'Australia/Brisbane' },
      { value: 'Australia/Broken_Hill', label: 'Australia/Broken Hill' },
      { value: 'Australia/Darwin', label: 'Australia/Darwin' },
      { value: 'Australia/Hobart', label: 'Australia/Hobart' },
      { value: 'Australia/Melbourne', label: 'Australia/Melbourne' },
      { value: 'Australia/Perth', label: 'Australia/Perth' },
      { value: 'Australia/Sydney', label: 'Australia/Sydney' },
    ],
  },
  {
    country: 'United Kingdom',
    zones: [{ value: 'Europe/London', label: 'Europe/London (UK — GMT/BST)' }],
  },
] as const;
const KNOWN_TIMEZONES: readonly string[] = TIMEZONE_GROUPS.flatMap((g) => g.zones.map((z) => z.value));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso));
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function UsersPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const token = session?.access_token ?? '';

  // ---- List state ----
  const [rows, setRows] = useState<readonly UserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setDebouncedSearch(search); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // ---- Add / Edit dialog ----
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // ---- Delete dialog ----
  const [deleteId, setDeleteId] = useState<string | null>(null);
  // Lost-phone reset target. Enrolment is never done from here — see the API's
  // /reset-mfa comment for why an admin can only REMOVE someone's factor.
  const [resetMfaRow, setResetMfaRow] = useState<{ id: string; email: string } | null>(null);
  const [resettingMfa, setResettingMfa] = useState(false);
  const [deleteLabel, setDeleteLabel] = useState('');
  const [deleting, setDeleting] = useState(false);

  // ---- Snackbar ----
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  // ---- Suburb autocomplete state ----
  const [suburbOptions, setSuburbOptions] = useState<readonly SuburbOption[]>([]);
  const [loadingSuburbs, setLoadingSuburbs] = useState(false);
  const suburbDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Search suburbs server-side as the user types (debounced 300ms, min 2 chars)
  const handleSuburbInputChange = useCallback((_e: React.SyntheticEvent, inputValue: string) => {
    if (suburbDebounceRef.current) clearTimeout(suburbDebounceRef.current);
    if (!form.state || inputValue.length < 2) {
      setSuburbOptions([]);
      return;
    }
    setLoadingSuburbs(true);
    suburbDebounceRef.current = setTimeout(() => {
      void (async () => {
        try {
          const res = await fetch(
            `${getApiUrl()}/api/v1/admin/postcodes/suburbs-by-abbrev?state=${encodeURIComponent(form.state)}&search=${encodeURIComponent(inputValue)}`,
            { headers: authHeaders(token) },
          );
          if (res.ok) {
            const json = (await res.json()) as { data: SuburbOption[] };
            setSuburbOptions(json.data);
          }
        } catch { /* silent */ }
        finally { setLoadingSuburbs(false); }
      })();
    }, 300);
  }, [form.state, token]);

  // ---------------------------------------------------------------------------
  // Fetch list
  // ---------------------------------------------------------------------------
  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setListError(null);
    try {
      const url = new URL(`${getApiUrl()}/api/v1/admin/users`);
      url.searchParams.set('page', String(page + 1));
      url.searchParams.set('limit', String(rowsPerPage));
      if (debouncedSearch) url.searchParams.set('search', debouncedSearch);

      const res = await fetch(url.toString(), { headers: authHeaders(token) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { data: UserRow[]; total: number };
      setRows(json.data);
      setTotal(json.total);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, debouncedSearch]);

  useEffect(() => { void fetchList(); }, [fetchList]);

  // ---------------------------------------------------------------------------
  // Open Add / Edit
  // ---------------------------------------------------------------------------
  const openAdd = () => {
    setEditingId(null);
    setForm(DEFAULT_FORM);
    setFormError(null);
    setSuburbOptions([]);
    setDialogOpen(true);
  };

  const openEdit = async (id: string) => {
    if (!token) return;
    setFormError(null);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/users/${encodeURIComponent(id)}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error('Failed to load user');
      const json = (await res.json()) as { data: UserRow };
      setForm({
        email: json.data.email,
        username: json.data.username,
        first_name: json.data.first_name,
        last_name: json.data.last_name,
        phone_number: json.data.phone_number,
        mobile_number: json.data.mobile_number,
        state: json.data.state,
        suburb: json.data.suburb,
        timezone: json.data.timezone,
        postcode: json.data.postcode,
        address: json.data.address,
        role: json.data.role,
        company_id: json.data.company_id != null ? String(json.data.company_id) : '',
        active: json.data.active,
      });
      // Pre-populate suburb option so the Autocomplete shows the current value in edit mode
      if (json.data.suburb) {
        setSuburbOptions([{ id: 0, suburb: json.data.suburb, postcode: json.data.postcode ?? '', country: 'AUSTRALIA' }]);
      } else {
        setSuburbOptions([]);
      }
      setEditingId(id);
      setDialogOpen(true);
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Failed to load user', 'error');
    }
  };

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------
  const handleSave = async () => {
    if (!form.email.trim()) { setFormError('Email is required.'); return; }

    setSaving(true);
    setFormError(null);
    try {
      const url = editingId
        ? `${getApiUrl()}/api/v1/admin/users/${encodeURIComponent(editingId)}`
        : `${getApiUrl()}/api/v1/admin/users`;

      const payload: Record<string, unknown> = {
        email: form.email.trim(),
        username: form.username.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone_number: form.phone_number.trim(),
        mobile_number: form.mobile_number.trim(),
        state: form.state,
        suburb: form.suburb.trim(),
        timezone: form.timezone,
        postcode: form.postcode.trim(),
        address: form.address.trim(),
        role: form.role,
        company_id: form.company_id || null,
        active: form.active,
      };

      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders(token),
        body: JSON.stringify(payload),
      });
      const responseJson = (await res.json()) as { error?: string; warnings?: string[] };
      if (!res.ok) {
        throw new Error(responseJson.error ?? `HTTP ${res.status}`);
      }
      setDialogOpen(false);
      if (responseJson.warnings && responseJson.warnings.length > 0) {
        showSnack(
          `User created with warnings: ${responseJson.warnings.join('; ')}`,
          'error',
        );
      } else {
        showSnack(editingId
          ? 'User updated.'
          : 'User created — magic-link invitation sent.');
      }
      void fetchList();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Reset two-factor (lost phone)
  // ---------------------------------------------------------------------------
  const handleResetMfa = async () => {
    if (!resetMfaRow || !token) return;
    setResettingMfa(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/v1/admin/users/${encodeURIComponent(resetMfaRow.id)}/reset-mfa`,
        { method: 'POST', headers: authHeaders(token) },
      );
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      setResetMfaRow(null);
      showSnack('Two-factor removed. They can set it up again from their account menu.');
      void fetchList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Reset failed', 'error');
    } finally {
      setResettingMfa(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------
  const confirmDelete = (id: string, label: string) => { setDeleteId(id); setDeleteLabel(label); };

  const handleDelete = async () => {
    if (!deleteId || !token) return;
    setDeleting(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/users/${encodeURIComponent(deleteId)}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      setDeleteId(null);
      showSnack('User deleted.');
      void fetchList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const totalPages = Math.ceil(total / rowsPerPage);

  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={() => navigate('/dashboard')} size="small">
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>Superadmin Users</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage superadmin accounts for the admin portal
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Superadmin
          </Button>
        </Box>

        {/* Toolbar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search email, name or username…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
            {search && (
              <Button size="small" onClick={() => { setSearch(''); setPage(0); }}>Clear</Button>
            )}
            <Box sx={{ ml: 'auto' }}>
              <Chip label={`${total} superadmins`} size="small" variant="outlined" />
            </Box>
          </Box>
        </Paper>

        {/* Error */}
        {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

        {/* Table */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
              <AppTableHead>
                <TableRow>
                  <AppTableHeadCell sx={{ width: '22%' }}>Email</AppTableHeadCell>
                  <AppTableHeadCell>Username</AppTableHeadCell>
                  <AppTableHeadCell>First Name</AppTableHeadCell>
                  <AppTableHeadCell>Last Name</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 100 }}>State</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 100 }}>Active</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 150 }}>Last Sign In</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 90 }}>2FA</AppTableHeadCell>
                  <AppTableHeadCell align="right" sx={{ width: 130 }}>Actions</AppTableHeadCell>
                </TableRow>
              </AppTableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No superadmins found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <AppTableRow key={row.id}>
                      <TableCell sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.email}
                      </TableCell>
                      <TableCell>{row.username || '—'}</TableCell>
                      <TableCell>{row.first_name || '—'}</TableCell>
                      <TableCell>{row.last_name || '—'}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{row.state || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.active ? 'Active' : 'Inactive'}
                          size="small"
                          color={row.active ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>{fmtDateTime(row.last_sign_in_at)}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.mfa_enabled ? 'On' : 'Off'}
                          size="small"
                          color={row.mfa_enabled ? 'success' : 'warning'}
                          variant={row.mfa_enabled ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {/* Reset only. Enrolment cannot be done for another user:
                            mfa.enroll() acts on the caller's own session, and the
                            TOTP secret must only ever reach the owner's phone.
                            This is the lost-phone path. */}
                        <Tooltip title={row.mfa_enabled ? 'Reset two-factor (lost phone)' : 'No two-factor enrolled'}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={!row.mfa_enabled}
                              onClick={() => setResetMfaRow({ id: row.id, email: row.email })}
                            >
                              <SecurityIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => void openEdit(row.id)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => confirmDelete(row.id, row.email)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </AppTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination bar */}
          <Box sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.palette.mode === 'light'
              ? alpha(theme.palette.primary.main, 0.09)
              : darken(theme.palette.background.paper, 0.35),
            px: 2,
            py: 1.5,
          })}>
            <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85 })}>
              {loading ? 'Loading…' : total === 0 ? 'No superadmins found' :
                `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, total)} of ${total} superadmins`
              }
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}>Show</Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                    '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                    '& .MuiSelect-select': { py: 0.5, px: 1 },
                  })}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </Box>

              {total > 0 && (
                <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}>
                  Page {page + 1} of {totalPages}
                </Typography>
              )}

              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  onChange={(_, p) => setPage(p - 1)}
                  color="primary"
                  size="small"
                  disabled={loading}
                  sx={(theme) => ({
                    '& .MuiPaginationItem-root': {
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${theme.palette.primary.main} !important`,
                      color: theme.palette.primary.contrastText,
                    },
                    '& .Mui-disabled': { opacity: 0.5 },
                  })}
                />
              )}
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* ------------------------------------------------------------------ */}
      {/* Add / Edit Dialog                                                    */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Superadmin' : 'Add Superadmin'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {formError && <Alert severity="error">{formError}</Alert>}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Email *"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              fullWidth
              autoFocus
              inputProps={{ maxLength: 255 }}
            />
            <TextField
              label="Username"
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              fullWidth
              inputProps={{ maxLength: 30 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="First Name"
              value={form.first_name}
              onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
              fullWidth
              inputProps={{ maxLength: 60 }}
            />
            <TextField
              label="Last Name"
              value={form.last_name}
              onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
              fullWidth
              inputProps={{ maxLength: 60 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Phone Number"
              value={form.phone_number}
              onChange={(e) => setForm((f) => ({ ...f, phone_number: e.target.value }))}
              fullWidth
              inputProps={{ maxLength: 20 }}
            />
            <TextField
              label="Mobile Number"
              value={form.mobile_number}
              onChange={(e) => setForm((f) => ({ ...f, mobile_number: e.target.value }))}
              fullWidth
              inputProps={{ maxLength: 20 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth sx={{ flex: 1 }}>
              <InputLabel id="state-label">State</InputLabel>
              <Select
                labelId="state-label"
                label="State"
                value={form.state}
                onChange={(e) => {
                  setForm((f) => ({ ...f, state: e.target.value, suburb: '', postcode: '' }));
                  setSuburbOptions([]);
                }}
              >
                <MenuItem value=""><em>Select State</em></MenuItem>
                {AU_STATES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              sx={{ flex: 2 }}
              options={suburbOptions as SuburbOption[]}
              loading={loadingSuburbs}
              disabled={!form.state}
              filterOptions={(x) => x}
              getOptionLabel={(o) => o.suburb}
              isOptionEqualToValue={(o, v) => o.suburb === v.suburb}
              value={suburbOptions.find((s) => s.suburb === form.suburb) ?? null}
              onInputChange={handleSuburbInputChange}
              noOptionsText={!form.state ? 'Select State first' : 'Type at least 2 characters…'}
              onChange={(_e, chosen) => {
                setForm((f) => ({
                  ...f,
                  suburb: chosen ? chosen.suburb : '',
                  postcode: chosen?.postcode ?? '',
                }));
              }}
              renderInput={(params) => (
                // @ts-expect-error: MUI Autocomplete renderInput params have optional props incompatible with exactOptionalPropertyTypes
                <TextField
                  {...params}
                  label="Suburb"
                  placeholder={
                    !form.state ? 'Select State first' :
                    loadingSuburbs ? 'Searching…' : 'Type to search…'
                  }
                />
              )}
            />
            <TextField
              label="Postcode"
              value={form.postcode}
              onChange={(e) => setForm((f) => ({ ...f, postcode: e.target.value }))}
              sx={{ flex: 0.7 }}
              inputProps={{ maxLength: 10, readOnly: !!form.suburb }}
            />
          </Box>

          <TextField
            label="Address"
            value={form.address}
            onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
            fullWidth
            inputProps={{ maxLength: 255 }}
          />

          <FormControl fullWidth>
            <InputLabel id="timezone-label">Timezone</InputLabel>
            <Select
              labelId="timezone-label"
              label="Timezone"
              value={form.timezone}
              onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
            >
              {TIMEZONE_GROUPS.flatMap((g) => [
                <ListSubheader key={`h-${g.country}`}>{g.country}</ListSubheader>,
                ...g.zones.map((tz) => (
                  <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                )),
              ])}
              {/* A stored zone outside the list still shows rather than a blank field. */}
              {form.timezone && !KNOWN_TIMEZONES.includes(form.timezone) ? (
                <MenuItem value={form.timezone}>{form.timezone}</MenuItem>
              ) : null}
            </Select>
          </FormControl>

          {!editingId && (
            <Alert severity="info" sx={{ mt: 0 }}>
              A magic-link invitation will be emailed to this address. The user
              signs in via the admin portal — no password needed.
            </Alert>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                color="success"
              />
            }
            label={form.active ? 'Active' : 'Inactive'}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={() => { void handleSave(); }} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : editingId ? 'Save Changes' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Delete Confirm Dialog                                                */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Superadmin</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete <strong>{deleteLabel}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => { void handleDelete(); }} disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset two-factor — the lost-phone path */}
      <Dialog open={Boolean(resetMfaRow)} onClose={() => setResetMfaRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Two-Factor</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Remove the authenticator app registered to <strong>{resetMfaRow?.email}</strong>?
            <Box sx={{ mt: 1.5 }}>
              They will sign in with just their password until they set it up again from their own
              account menu — so only do this when they have genuinely lost access to their phone.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResetMfaRow(null)} disabled={resettingMfa}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={() => { void handleResetMfa(); }} disabled={resettingMfa}>
            {resettingMfa ? <CircularProgress size={18} /> : 'Reset'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
